import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Icon,
    Avatar
} from '@mui/material';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import Swal from 'sweetalert2';
import axios from 'axios';
import { URL } from 'constants/constant';
import { QRCodeSVG } from 'qrcode.react';

// Main Component
const OrganisersManagement = () => {
    const [selectedOrganiser, setSelectedOrganiser] = useState({ name: "", image: "", category: "", country: "", type: "", desc: "" });
    const [open, setOpen] = useState(false);
    const [organisers, setOrganisers] = useState([]);
    const [formType, setFormType] = useState('add');

    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrValue, setQrValue] = useState('');

    const handleOpen = (type, item = { name: "", image: "", category: "", country: "", type: "", desc: "" }) => {
        setFormType(type);
        setSelectedOrganiser(item);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSave = async (data) => {
        try {
            const formData = new FormData();
            formData.append("name", data.name);
            formData.append("type", data.type);
            formData.append("country", data.country);
            formData.append("desc", data.desc || "");
            if (data.profilePic instanceof File) {
                formData.append("profilePic", data.profilePic);
            }

            if (formType === 'add') {
                await axios.post(`${URL}/api/admin/exhibitor`, formData);
                Swal.fire("Success!", "Exhibitor added successfully!", "success");
            } else {
                await axios.patch(`${URL}/api/admin/exhibitor/${data._id}`, formData);
                Swal.fire("Success!", "Exhibitor updated successfully!", "success");
            }

            handleClose();
            getExhibitors();
        } catch (error) {
            console.error("Error saving exhibitor:", error);
            Swal.fire("Error!", "Failed to save exhibitor.", "error");
            handleClose();
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will delete the exhibitor permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${URL}/api/admin/exhibitor/${id}`);
                    Swal.fire("Deleted!", "The exhibitor has been deleted.", "success");
                    getExhibitors();
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error!", "Failed to delete exhibitor.", "error");
                }
            }
        });
    };

    const getExhibitors = async () => {
        try {
            const response = await axios.get(`${URL}/api/admin/exhibitor`);
            if (response.data) {
                setOrganisers(response.data.data.reverse());
            }
        } catch (err) {
            console.error("Error fetching exhibitors:", err);
            Swal.fire("Error!", "Failed to fetch exhibitors.", "error");
        }
    };

    useEffect(() => {
        getExhibitors();
    }, []);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Box display={'flex'} mb={3} justifyContent={'end'}>
                <Button startIcon={<Icon>add</Icon>} style={{ color: 'white' }} onClick={() => handleOpen('add')} variant='contained' color='primary'>
                    Add Exhibitor
                </Button>
            </Box>

            <AptaTable
                columns={['Name', 'Country', 'Type', 'Description', 'Qr Code', 'Actions']}
                rows={organisers?.map((item) => ({
                    Name: <MDTypography variant="caption">{item?.name}</MDTypography>,
                    Country: <MDTypography variant="caption">{item?.country}</MDTypography>,
                    Type: <MDTypography variant="caption">{item?.type}</MDTypography>,
                    Description: <MDTypography variant="caption">{item?.desc}</MDTypography>,
                    'Qr Code': <Button color="secondary" onClick={() => {
                        setQrValue(item?._id);
                        setQrModalOpen(true);
                    }}>
                        <Icon>qr_code</Icon>
                    </Button>,
                    Actions: (
                        <ButtonGroup size="small">
                            <Button color='info' onClick={() => handleOpen('edit', item)}>
                                <Icon color='info'>edit</Icon>
                            </Button>
                            <Button color="error" onClick={() => handleDelete(item?._id)}>
                                <Icon color='error'>delete</Icon>
                            </Button>
                        </ButtonGroup>
                    )
                }))}
            />

            <InputFormModal
                open={open}
                onClose={handleClose}
                onSave={handleSave}
                details={selectedOrganiser}
                type={formType}
            />

            <QRModal open={qrModalOpen} onClose={() => setQrModalOpen(false)} value={qrValue} />
        </DashboardLayout>
    );
};

export default OrganisersManagement;

// InputFormModal Component
const InputFormModal = ({ open, onClose, onSave, details, type }) => {
    const [formData, setFormData] = useState({ name: "", profilePic: "", type: "", country: "", desc: "" });
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (details) setFormData(details);
    }, [details]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, profilePic: file });
        }
    };

    const handleSave = () => {
        onSave(formData);
    };

    const isSaveDisabled = () => {
        return !formData.name || !formData.country || !formData.type;
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>{type === 'add' ? 'Add Exhibitor' : 'Edit Exhibitor'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
                    <Box sx={{ mt: 3, mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleImageChange} />
                        <Avatar
                            src={
                                formData.profilePic
                                    ? typeof formData.profilePic === "string"
                                        ? `${URL}/${formData.profilePic}`
                                        : window.URL.createObjectURL(formData.profilePic)
                                    : null
                            }
                            sx={{
                                width: 120,
                                height: 120,
                                cursor: "pointer",
                                bgcolor: formData.profilePic ? "transparent" : "grey.300",
                                fontSize: 64,
                            }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {!formData.profilePic && <AccountCircleIcon sx={{ fontSize: 80, color: "grey.600" }} />}
                        </Avatar>
                        <Typography variant="caption" color="textSecondary" sx={{ cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
                            Click avatar to upload profile picture
                        </Typography>
                    </Box>

                    <TextField
                        label="Name"
                        name="name"
                        type="text"
                        value={formData.name || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Country"
                        name="country"
                        type="text"
                        value={formData.country || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Description"
                        name="desc"
                        type="text"
                        value={formData.desc || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="type-select-label">Type</InputLabel>
                        <Select
                            labelId="type-select-label"
                            id="type-select"
                            name="type"
                            value={formData.type || ''}
                            label="Type"
                            onChange={handleChange}
                            style={{ minHeight: 40 }}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {["bronze", "silver", "gold", "platinum", "super platinum", "diamond"].map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary">Cancel</Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={isSaveDisabled()} sx={{ color: 'white' }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// QR Modal Component
const QRModal = ({ open, onClose, value }) => {
    const qrCodeRef = useRef(null);

    const handleDownload = () => {
        if (qrCodeRef.current) {
            const canvas = document.createElement('canvas');
            const svg = qrCodeRef.current.querySelector('svg');

            if (svg) {
                const svgData = new XMLSerializer().serializeToString(svg);
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);

                    const pngFile = canvas.toDataURL('image/png');
                    const downloadLink = document.createElement('a');
                    downloadLink.href = pngFile;
                    downloadLink.download = `qrcode_${value}.png`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                };

                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Exhibitor QR Code</DialogTitle>
            <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                <Box ref={qrCodeRef} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    {value ?
                        <QRCodeSVG
                            value={value}
                            size={128}
                        />
                        : <Typography>No QR data</Typography>}
                </Box>
                <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>{value}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDownload} style={{ color: 'white' }} color="primary" variant="contained">Download QR</Button>
            </DialogActions>
        </Dialog>
    );
};
